'use client'

import { setGADataLayer } from 'analytics'
import { MAX_FILE_COUNT } from 'creation/src/creation-tools/constants'
import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import CreationToolError from 'creation/src/creation-tools/types/error'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import CreationToolWelcomeButton from 'creation/src/creation-tools/types/welcome-button'
import getUnvalidatedSource from 'creation/src/creation-tools/utils/get-unvalidated-source'
import { validateSourceByInput } from 'creation/src/creation-tools/utils/validate-source'
import { useContext, type ReactNode, type SyntheticEvent } from 'react'
import { useCreationStore } from '../store/provider'
import DropZone from './drop-zone'
import { WelcomeContext } from './welcome-context'

type Props = {
    children?: ReactNode
    className?: string
    button: CreationToolWelcomeButton
    isStaff: boolean
}

/**
 * Legacy structural reference: components/welcome/buttons/button/file.tsx.
 *
 * The `CreationToolWelcomeButton.UploadAddMore` branch (bulk "add more
 * files" after upload) is dropped — `CreationTool.welcomeButtons()` never
 * produces that button on this screen (see presentation/icons.ts), so it's
 * dead code here.
 *
 * Legacy also routes validation failures through `CreationToolsWelcomeContext`
 * (`setError`/`setErrorSource`/`setIsLoading`) to drive an error modal and a
 * loading state — wired below via `WelcomeContext` (Task 17, Task 3 of this
 * fix pass). The nested try/catch below mirrors legacy's own nesting
 * exactly (not a simplification of it):
 *
 * - The outer catch only ever sees an `Error` with a plain string message
 *   — either `'too many files'` (TooManyFiles branch) or
 *   `CreationToolError.gaLabel(error, file)` (single-file branch, e.g. the
 *   rejected file's MIME type for most validation failures) — and forwards
 *   it verbatim as `formError`. It does NOT call `setError`/`setErrorSource`
 *   itself; those are set by whichever inner branch actually threw, with
 *   the exact value that branch needs (`CreationToolError.TooManyFiles` for
 *   the count check; the real caught `CreationToolError` plus `file` for
 *   the single-file validation failure). Flattening this into one catch
 *   would either lose the gaLabel translation or wrongly call
 *   `setError`/`setErrorSource` on the TooManyFiles path, which legacy
 *   never does.
 * - `setIsLoading(true)`/`setIsLoading(false)` bracket only the single-file
 *   validate call, matching legacy's placement exactly — not the
 *   TooManyFiles check (which fails before any request is made) and not
 *   the multi-file branch (`setFiles` there is synchronous, no network
 *   round-trip to show a loader for).
 */
const FileButton = ({ children, className, button, isStaff }: Props) => {
    const tool = useCreationStore((state) => state.tool)
    const setFiles = useCreationStore((state) => state.setFiles)
    const { setError, setErrorSource, setIsLoading } = useContext(WelcomeContext)

    const outputType = CreationToolWelcomeButton.outputType(button)
    const mimeTypes = CreationToolWelcomeButton.mimeTypes(button, isStaff)

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget
        if (!files || !files.length) return

        const buttonTitle = CreationToolWelcomeButton.title(button)
        const file = files[0]
        const fileExtension = file?.name.split('.').pop()
        const isGifMaker = tool === CreationTool.GifMaker

        // Legacy bug, ported verbatim (spec §8.1): `file_selection_open`
        // double-nests `options.options.form` while `_success`/`_error`
        // below use the flat `options.form` shape. Do not "fix" this here.
        setGADataLayer({
            event: 'file_selection_open',
            options: {
                options: {
                    form: {
                        formName: 'choose file',
                    },
                },
                content: {
                    contentFormat: buttonTitle,
                    contentType: fileExtension,
                },
            },
        })

        try {
            if (files.length > MAX_FILE_COUNT) {
                setError(CreationToolError.TooManyFiles)
                throw new Error('too many files')
            }

            if (files.length === 1) {
                const requiresFrameProcessing = isGifMaker && button === CreationToolWelcomeButton.Sticker
                setIsLoading(true)

                try {
                    // If the file will be processed into an array of frames, we don't need to
                    // worry about validating audio or transparency up front — that's accounted
                    // for automatically by the tool's creation steps (e.g. Cut Out).
                    const validatedFile = await validateSourceByInput(file, mimeTypes, {
                        requiresAudio: !requiresFrameProcessing && CreationToolOutputType.requiresAudio(outputType),
                        requiresTransparency:
                            !requiresFrameProcessing && CreationToolOutputType.requiresTransparency(outputType),
                    })
                    setFiles([validatedFile], outputType)
                } catch (err) {
                    setError(err as CreationToolError)
                    setErrorSource(file)
                    throw new Error(CreationToolError.gaLabel(err as CreationToolError, file))
                }

                setIsLoading(false)
            } else {
                const newSources = Array.from(files).map(getUnvalidatedSource)
                setFiles(newSources, outputType)
            }

            setGADataLayer({
                event: 'file_selection_success',
                options: {
                    form: {
                        formName: 'choose file',
                    },
                    content: {
                        contentFormat: buttonTitle,
                        contentType: fileExtension,
                    },
                },
            })
        } catch (err) {
            setGADataLayer({
                event: 'file_selection_error',
                options: {
                    form: {
                        formName: 'choose file',
                        formError: err,
                    },
                    content: {
                        contentFormat: buttonTitle,
                        contentType: fileExtension,
                    },
                },
            })
        }
    }

    return (
        <DropZone tool={tool} className={className}>
            {children}
            <input
                type="file"
                className="absolute inset-0 m-0 h-full w-full cursor-pointer p-0 opacity-0"
                accept={mimeTypes.join(',')}
                multiple={CreationTool.supportsMultipleFiles(tool)}
                onInput={onFileInput}
            />
        </DropZone>
    )
}

export default FileButton
